// app/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import initSession from '@/app/lib/init-session';
import { resetAppState } from '@/app/lib/reset-app-state';
import { createClient } from '@/shared/api';
import {
  BIOCARE_PORTAL_MERCY_URL,
  BIOCARE_PORTAL_URL,
  CLIENT_NAMES,
  EMITTER_CONSTANTS,
  ERROR_ACTION,
  LOADING_TYPES,
  ORGANIZATION_SLUG,
  PROGRAMS,
  USER_VERIFICATION_RELEASE_DATE,
  INTERGRATION_ORG,
  LINK_REDIRECT_AWS_CARDIO_ONE_PORTAL,
  URL_ERP_PORTAL,
} from '@/shared/config';
import { useLoadingStore } from '@/shared/model';
import { handleLogout, useUserStore, handleCheckRoles, CONFIG } from '@/entities/user';
import {
  isWebViewCreateStudy,
  auth,
  cookies,
  COOKIE_NAMES,
  emitter,
  removeFreshChatWidget,
  SocketIo,
  GETTEXT_ALL_FACILITIES,
} from '@/shared/lib';
import {
  useEmitter,
  useForceRerender,
  useMergeState,
  usePrevRender,
} from '@/shared/lib/customHooks';
import { Amplify, Auth } from 'aws-amplify';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import {
  updatedAwsConfigBiocarePortal,
  updatedAwsConfigErpPortal as updatedAwsConfig,
  updatedAwsConfigMercy,
} from '@/shared/aws';
import { facilitySelection } from '@/entities/facility';
import {
  clearCookies,
  messageHandler,
  removeScriptMessage,
  updateSignOutRedirect,
} from './authenticationGate.handler';

/**
 * Drives the authentication gate wrapping the portal: session detection, sign-out routing and the account-state modals. Holds the merged modal and session state, resolves the Cognito pool from cookies on mount, subscribes the sign-out router to LOGOUT_REQUEST plus handlers for facility, program, user, access-removed, expired-token, token-update, What's New, maintenance and unauthorized events, and exposes the sign-out and popup callbacks.
 *
 * @returns The gate state together with the sign-out, redirect and popup callbacks it exposes.
 */
export const useAuthenticationGate = () => {
  /**
   * Holds the current user profile taken from the user store. Subscribes to the me slice of useUserStore so the authentication gate re-renders when the signed-in user's data changes.
   *
   * @returns The me object currently held in the user store.
   */
  const meRedux = useUserStore((s) => s.me);

  /**
   * Holds the store action that sets the global loading text. Read from useLoadingStore's setLoadingText so the gate can raise and clear the full-page loading overlay.
   *
   * @returns The setLoadingText action from the loading store.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);

  const forceRerender = useForceRerender();

  const [state, setState] = useMergeState({
    isLoadedData: false,
    isLoginWithBiocarePortal: false,
    isOpenVerifyEmailModal: false,
    verifyEmailDeadline: null as string | null,
    isOpenWhatNewPopup: JSON.parse(auth.getWillShowWhatNewPopup()),
    isUnauthorized: false,
    hasCheckedLoginStatus: false,
    isOpenAccessRemovedModal: false,
    isOpenPasswordChangedModal: false,
    isOpenDeactivatedAccountModal: false,
    isOpenEventBucketsChangedModal: false,
    isOpenClosedClinicModal: false,
    isOpenExpiredTokenModal: false,
    isSigningInAgain: false,
    isAccessNotAvailable: false,
  });

  const listenerRef = React.useRef<any>(undefined);
  const timeOutShowChangePassword = useRef<any>(null);
  const isSigningOut = useRef(false);

  /**
   * Indicates the session was started from the Admin or Sales portal rather than by a direct login. True when CONFIG.IS_LOGIN_THROUGH_ADMIN or CONFIG.IS_LOGIN_THROUGH_SALES is set, or when auth reports an admin/sales role.
   *
   * @returns True for an admin or sales impersonated session.
   */
  const isLoginThroughAdminOrSales =
    !!CONFIG.IS_LOGIN_THROUGH_ADMIN || !!CONFIG.IS_LOGIN_THROUGH_SALES || auth.isAdminOrSales();
  const willViewTutorialLater = JSON.parse(auth.getWillViewTutorialLater());
  const willShowWhatNewPopup = JSON.parse(auth.getWillShowWhatNewPopup());
  const loginFrom = auth.getLoginFrom();

  /**
   * Brings the portal online after the auth tokens in localStorage changed. Connects the socket to the server and re-runs the session initialisation.
   *
   * @returns Nothing.
   */
  const changeLocalStorage = () => {
    (SocketIo as any).connectToServer();
    initSession();
  };

  /**
   * Asks the embedded ERP portal to sign the shared session out. Posts a signOutErp action into the content window of the element with id 'iframe-id' when that iframe exists.
   *
   * @returns Nothing.
   */
  const postMessageToErp = () => {
    const iframe = document.getElementById('iframe-id') as any;
    if (iframe) {
      iframe.contentWindow.postMessage(
        {
          action: 'signOutErp',
        },
        '*',
      );
    }
  };

  /**
   * Signs a directly authenticated user out of the portal. Shows the signing-out loading text, calls the logout mutation, disconnects the socket, expires the cookies, resets the Apollo store and the app state, removes the injected message script when present, then clears the loading text and the in-progress flag.
   *
   * @returns A promise resolving once the sign-out sequence has completed.
   */
  const handleSignOut = async () => {
    isSigningOut.current = true;
    loadingPageAction(LOADING_TYPES.SIGNING_OUT);
    removeFreshChatWidget();
    try {
      await (handleLogout as any)();
    } catch (error) {
      console.error('Failed to logout: ', error);
    }
    (SocketIo as any).disconnectSocket();
    clearCookies();
    const client = createClient(true as any);
    client.resetStore();
    resetAppState();
    if ((document as any).idMessage) {
      removeScriptMessage();
    }
    loadingPageAction();
    isSigningOut.current = false;
  };

  /**
   * Ends the BioCare-portal session without navigating away. Disconnects the socket, calls the logout mutation with the current refresh token, awaits Auth.signOut, removes the FreshChat widget and the injected message script, and resets the Apollo store, rethrowing any failure as an Error.
   *
   * @returns A promise resolving once the session teardown has finished.
   */
  const handleOnlySignOutBiocarePortal = async () => {
    try {
      (SocketIo as any).disconnectSocket();
      try {
        const refreshToken = (await Auth.currentSession()).getRefreshToken().getToken();
        await handleLogout({ refreshToken });
      } catch (error) {
        console.error('Failed to logout: ', error);
      }
      await Auth.signOut();
      removeFreshChatWidget();
      if ((document as any).idMessage) {
        removeScriptMessage();
      }
      const client = createClient(true as any);
      client.resetStore();
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error));
    }
  };

  /**
   * Clears the local session and sends the user to the sign-in page of the portal they came from. Preserves the saved locale and loginFrom across auth.logout, expires the domain cookies, then navigates to the CardioOne AWS portal when integrationBy is CardioOne and otherwise to the Mercy or default BioCare sign-in URL.
   *
   * @returns Nothing.
   */
  const handleRedirectToBiocarePortal = () => {
    const isCardioOne = auth.getIntegrationBy() === INTERGRATION_ORG.CARDIO_ONE;
    const currentLocale = auth.getLocale();
    auth.logout();
    auth.saveLocale(currentLocale);
    auth.setLoginFrom(loginFrom);
    clearCookies();
    window.location.href = isCardioOne
      ? LINK_REDIRECT_AWS_CARDIO_ONE_PORTAL
      : loginFrom === 'Mercy'
        ? `${BIOCARE_PORTAL_MERCY_URL}sign-in`
        : `${BIOCARE_PORTAL_URL}sign-in`;
  };

  /**
   * Signs a BioCare-portal user out and returns them to that portal. Shows the signing-out loading text while awaiting the session teardown and logs any failure, then always redirects to the portal sign-in page and clears the loading state.
   *
   * @returns A promise resolving once sign-out and the redirect have been triggered.
   */
  const handleSignOutBiocarePortal = async () => {
    try {
      isSigningOut.current = true;
      loadingPageAction(LOADING_TYPES.SIGNING_OUT);
      await handleOnlySignOutBiocarePortal();
    } catch (error) {
      console.error('Failed to sign out: ', error);
    } finally {
      isSigningOut.current = false;
      handleRedirectToBiocarePortal();
      loadingPageAction();
    }
  };

  /**
   * Routes a LOGOUT_REQUEST emitter event to the sign-out path matching how the user logged in. Sends the create-study webview to the biotres failure deep link, delegates a BioCare-portal session to its own sign-out, and otherwise posts a sign-out message to the ERP iframe for the LOGOUT action or performs a full local sign-out for REDIRECT.
   *
   * @param errorAction - Action carried by the logout request.
   * @returns Nothing.
   */
  const signOutEvent = (errorAction: any) => {
    if (isWebViewCreateStudy()) {
      window.location.href = 'biotres://end-createstudy/fail';
      return;
    }

    if (state.isLoginWithBiocarePortal) {
      handleSignOutBiocarePortal();
      return;
    }

    // Handle log out by ERP
    switch (errorAction) {
      case ERROR_ACTION.LOGOUT: {
        postMessageToErp();
        break;
      }
      case ERROR_ACTION.REDIRECT: {
        handleSignOut();
        break;
      }
      default: {
        break;
      }
    }
  };

  // Write as a single function cuz will remove the old method later
  /**
   * Legacy detection of which Cognito pool the visitor is signed into, driven by the 'targetPool' cookie. Configures Amplify for Mercy when that cookie is 'Mercy' and for the common BioCare pool otherwise, records loginFrom and marks the session as a BioCare-portal login; on failure it retries the common pool, then emits EXPIRED_TOKEN for a known user or redirects to sign-in, always setting hasCheckedLoginStatus.
   *
   * @returns A promise resolving once the login status has been resolved.
   */
  const handleCheckLoginStatusOld = async () => {
    try {
      const targetPool = cookies.getByNameFragment(COOKIE_NAMES.targetPool);

      const isMercyLogin = targetPool === 'Mercy';
      if (isMercyLogin) {
        Amplify.configure(updatedAwsConfigMercy());
        await Auth.currentAuthenticatedUser({ bypassCache: true });
        auth.setLoginFrom('Mercy');
        auth.setIsLoginWithBiocarePortal(true);
        setState({ isLoginWithBiocarePortal: true });
        return;
      }

      Amplify.configure(updatedAwsConfigBiocarePortal());
      await Auth.currentAuthenticatedUser({ bypassCache: true });
      auth.removeLoginFrom();
      auth.setIsLoginWithBiocarePortal(true);
      setState({ isLoginWithBiocarePortal: true });
    } catch (error) {
      console.error('Failed to get current Mercy authenticated user: ', error);
      try {
        Amplify.configure(updatedAwsConfigBiocarePortal());
        await Auth.currentAuthenticatedUser({ bypassCache: true });
        auth.removeLoginFrom();
        auth.setIsLoginWithBiocarePortal(true);
        setState({ isLoginWithBiocarePortal: true });
      } catch (error) {
        console.error('Failed to get current Common authenticated user: ', error);
        if (auth.userId()) {
          emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN);
        } else {
          auth.removeUserId();
          auth.removeLoginFrom();
          window.location.href =
            loginFrom === 'Mercy'
              ? `${BIOCARE_PORTAL_MERCY_URL}sign-in`
              : `${BIOCARE_PORTAL_URL}sign-in`;
        }
      }
    } finally {
      setState({ hasCheckedLoginStatus: true });
    }
  };

  /**
   * Detects which organisation's Cognito pool the visitor is signed into from the 'organization' cookie. Falls back to the legacy targetPool check when the cookie is absent, configures Amplify for Mercy or the common BioCare pool, repoints the sign-out redirect when the username prefix marks a CardioOne integration, and on failure emits EXPIRED_TOKEN for a known user or redirects to sign-in.
   *
   * @returns A promise resolving once the login status has been resolved.
   */
  const handleCheckLoginStatus = async () => {
    try {
      const organization = cookies.getByNameFragment(COOKIE_NAMES.organization);

      if (!organization) {
        // in case organization cookie is not set, fallback to old method
        handleCheckLoginStatusOld();
        return;
      }
      const isMercyLogin = organization === ORGANIZATION_SLUG.MERCY;
      if (isMercyLogin) {
        Amplify.configure(updatedAwsConfigMercy());
        await Auth.currentAuthenticatedUser({ bypassCache: true });
        auth.setLoginFrom('Mercy');
        auth.setIsLoginWithBiocarePortal(true);
        setState({ isLoginWithBiocarePortal: true });
        return;
      }
      auth.removeIntegrationBy();
      Amplify.configure(updatedAwsConfigBiocarePortal());
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const { username } = user || {};
      if (username) {
        const integrationBy = username.split('_')?.[0] || '';
        if (integrationBy === INTERGRATION_ORG.CARDIO_ONE) {
          auth.setIntegrationBy(integrationBy);
          updateSignOutRedirect(LINK_REDIRECT_AWS_CARDIO_ONE_PORTAL);
        }
      }
      auth.removeLoginFrom();
      auth.setIsLoginWithBiocarePortal(true);
      setState({ isLoginWithBiocarePortal: true });
    } catch (error) {
      console.error('Failed to get current authenticated user: ', error);

      // *: If user has logged in before, but now the access token is expired or invalid, display the expired token modal, if not logged in, redirect to the login page
      if (auth.userId()) {
        emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN);
      } else {
        auth.removeUserId();
        auth.removeLoginFrom();
        window.location.href =
          loginFrom === 'Mercy'
            ? `${BIOCARE_PORTAL_MERCY_URL}sign-in`
            : `${BIOCARE_PORTAL_URL}sign-in`;
      }
    } finally {
      setState({ hasCheckedLoginStatus: true });
    }
  };

  /**
   * Dismisses the email-verification reminder modal. Sets isOpenVerifyEmailModal to false in the hook state.
   *
   * @returns Nothing.
   */
  const onCancelVerifyEmailModal = () => {
    setState({ isOpenVerifyEmailModal: false });
  };

  /**
   * Handles the notification that the user's password was changed elsewhere. Cancels any pending timer and schedules one 500 ms later that clears the stored session except the username and opens the password-changed modal.
   *
   * @returns Nothing.
   */
  const handleIsChangePassword = () => {
    if (timeOutShowChangePassword.current) {
      clearTimeout(timeOutShowChangePassword.current);
      timeOutShowChangePassword.current = null;
    }
    timeOutShowChangePassword.current = setTimeout(() => {
      auth.logoutExceptUsername();
      setState({ isOpenPasswordChangedModal: true });
    }, 500);
  };

  /**
   * Controls visibility of the What's New release popup. Merges the given flag into the hook state as isOpenWhatNewPopup.
   *
   * @param isOpenWhatNewPopup - Whether the What's New popup should be open.
   * @returns Nothing.
   */
  const displayWhatNewPopup = (isOpenWhatNewPopup: any) => {
    setState({ isOpenWhatNewPopup });
  };

  /**
   * Turns a socket-driven sign-out instruction into a logout request on the emitter. Emits LOGOUT_REQUEST with the REDIRECT action when a redirect is wanted and with LOGOUT otherwise.
   *
   * @param shouldRedirect - Whether the sign-out should redirect instead of logging out in place.
   * @returns Nothing.
   */
  const handleSignOutBySocket = (shouldRedirect = false) => {
    if (shouldRedirect) {
      emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
    } else {
      emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.LOGOUT);
    }
  };

  /**
   * Navigates the browser back to the BioCare portal the user arrived from. Chooses the Mercy portal URL when loginFrom is 'Mercy' and the default BioCare portal URL otherwise.
   *
   * @returns Nothing.
   */
  const handleBackToBiocarePortal = () => {
    window.location.href = loginFrom === 'Mercy' ? BIOCARE_PORTAL_MERCY_URL : BIOCARE_PORTAL_URL;
  };

  /**
   * Starts a fresh sign-in after the session ended. Marks the hook as signing in again, then navigates to the ERP portal for a Microsoft-account login and otherwise to the BioCare portal sign-in page.
   *
   * @returns Nothing.
   */
  const handleSignInAgainClick = () => {
    setState({ isSigningInAgain: true });

    if (auth.isLoginWithMicrosoftAccount()) {
      window.location.href = URL_ERP_PORTAL;
      return;
    }

    handleRedirectToBiocarePortal();
  };

  /**
   * Reacts to the socket reporting that the signed-in account was deactivated. Disconnects the socket, awaits Auth.signOut while only logging a failure, then opens the deactivated-account modal.
   *
   * @returns A promise resolving once sign-out has been attempted and the modal opened.
   */
  const handleDeactivatedAccountViaSocket = async () => {
    (SocketIo as any).disconnectSocket();
    try {
      await Auth.signOut();
    } catch (error) {
      console.error('Failed to sign out (deactivated account): ', error);
    }
    setState({ isOpenDeactivatedAccountModal: true });
  };

  /**
   * Confirms the deactivated-account modal. Clears the local session and redirects to the originating BioCare portal sign-in page, then closes the modal.
   *
   * @returns A promise resolving once the redirect has been triggered and the modal closed.
   */
  const handleOkayDeactivatedAccountModal = async () => {
    handleRedirectToBiocarePortal();
    setState({ isOpenDeactivatedAccountModal: false });
  };

  usePrevRender(() => {
    if (!state.isLoginWithBiocarePortal) {
      window.addEventListener('message', messageHandler, false);
    }
    const listener = emitter.addListener(EMITTER_CONSTANTS.LOGOUT_REQUEST, signOutEvent);
    return () => {
      listener.remove();
      window.removeEventListener('message', messageHandler, false);
    };
  }, [state.isLoginWithBiocarePortal]);

  useEffect(() => {
    if (isWebViewCreateStudy()) {
      return;
    }

    if (isLoginThroughAdminOrSales) {
      Amplify.configure(updatedAwsConfig());
      setState({ hasCheckedLoginStatus: true });
      return;
    }

    handleCheckLoginStatus();
  }, []);

  useEffect(
    () => () => {
      if (!state.isLoginWithBiocarePortal) {
        window.removeEventListener('message', messageHandler, false);
      }
      if (listenerRef.current) {
        listenerRef.current?.remove();
      }
    },
    [],
  );

  useEffect(() => {
    if (state.isLoginWithBiocarePortal) {
      (SocketIo as any).connectToServer();
      initSession();
    }
  }, [state.isLoginWithBiocarePortal]);

  useEffect(() => {
    if (!_.isEmpty(meRedux)) {
      const isValid = handleCheckRoles(meRedux?.roles);
      if (isValid) {
        setState({ isLoadedData: true });
      } else {
        setState({ isOpenAccessRemovedModal: true });
      }

      // supportFacilities rỗng nhưng nếu C1C thì là not access, còn user thường là Closed Facility
      const integrationBy = auth.getIntegrationBy();
      if (_.isEmpty(meRedux?.supportFacilities) && integrationBy === INTERGRATION_ORG.CARDIO_ONE) {
        setState({ isAccessNotAvailable: true });
      }
    }
  }, [meRedux]);

  useEffect(() => {
    if (state.isLoadedData) {
      if (state.isLoginWithBiocarePortal) {
        if (!_.isEmpty(meRedux)) {
          if (!meRedux.isEmailVerified) {
            const lastModifiedDate = meRedux.lastChangeEmail || meRedux.createdAt;
            const userVerificationReleaseDate = USER_VERIFICATION_RELEASE_DATE;
            const isCreatedAfterReleaseDate = dayjs(lastModifiedDate)
              .endOf('d')
              .isAfter(dayjs(userVerificationReleaseDate).endOf('d'));
            if (isCreatedAfterReleaseDate) {
              setState({
                isOpenVerifyEmailModal: true,
                verifyEmailDeadline: dayjs(lastModifiedDate).add(30, 'd').toISOString(),
              });
            } else {
              setState({
                isOpenVerifyEmailModal: true,
                verifyEmailDeadline: dayjs(userVerificationReleaseDate).add(30, 'd').toISOString(),
              });
            }
          }
        }
      }
    }
  }, [state.isLoadedData, meRedux.isEmailVerified, meRedux.createdAt, meRedux.lastChangeEmail]);

  useEmitter(
    EMITTER_CONSTANTS.FACILITY_SETTING_UPDATED,
    (msg: any) => {
      if (msg?.facilityId && !_.isNil(msg?.settingInfo?.canViewFlaggedStudies)) {
        const userSupportFacilities = facilitySelection.userSupportFacilities();
        /**
         * Holds the user's support facility matching the facilityId carried by a facility-setting-updated socket message. Looked up with lodash find over facilitySelection.userSupportFacilities(), so it is undefined when the updated facility is not one of the user's own.
         *
         * @returns The matching support facility, or undefined.
         */
        const foundSelectedFacility = _.find(userSupportFacilities, (x) => x.id === msg.facilityId);
        if (foundSelectedFacility && !auth.isClinicPhysician()) {
          window.location.reload();
        }
      }
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.PROGRAMS_UPDATED,
    (msg: any) => {
      if (msg?.id) {
        const userSupportFacilities = facilitySelection.userSupportFacilities();
        const selectedFacility = facilitySelection.selectedFacilityLocalStorage();
        /**
         * Holds the user's support facility matching the id carried by a programs-updated socket message. Looked up with lodash find over facilitySelection.userSupportFacilities(), so it is undefined when the changed facility is not assigned to the user.
         *
         * @returns The matching support facility, or undefined.
         */
        const foundSelectedFacility = _.find(userSupportFacilities, (x) => x.id === msg.id);
        if (foundSelectedFacility) {
          if (foundSelectedFacility.id === selectedFacility.value) {
            if (msg.program === PROGRAMS.PROTECT_PLUS) {
              selectedFacility.programs.protectPlus = msg.isEnabled;
            } else {
              selectedFacility.programs.renewalStock = msg.isEnabled;
            }
            facilitySelection.setSelectedFacilityLocalStorage(selectedFacility);
          }
          // window.location.reload();
        }
      }
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.USER_UPDATED,
    (msg: any) => {
      if (msg.id === auth.userId()) {
        // *: Deactivated account
        if (msg.isDisabled) {
          handleDeactivatedAccountViaSocket();
          return;
        }

        // *: Change event buckets from Admin Portal
        if (msg.isBucketChanged) {
          setState({ isOpenEventBucketsChangedModal: true });
          return;
        }

        // *: Access removed
        if (msg.roles) {
          if (state.isLoginWithBiocarePortal) {
            const isValid = handleCheckRoles(msg.roles);
            if (!isValid) {
              setState({ isOpenAccessRemovedModal: true });
              return;
            }
          }
        }

        // *: Closed clinic
        if (msg.facility?.status) {
          if (msg.facility.status === 'Closed') {
            const userSupportFacilities = facilitySelection.userSupportFacilities(); // already remove closed clinic
            /**
             * Holds the user's support facility matching the facility id of a user-updated socket message reporting a Closed clinic. Looked up with lodash find over facilitySelection.userSupportFacilities(), which already excludes closed clinics, so a miss means the closure does not affect this user.
             *
             * @returns The matching support facility, or undefined.
             */
            const foundFacility = _.find(userSupportFacilities, (x) => x.id === msg.facility?.id);
            if (foundFacility) {
              if (userSupportFacilities.length === 1) {
                const integrationBy = auth.getIntegrationBy();
                if (integrationBy === INTERGRATION_ORG.CARDIO_ONE) {
                  setState({ isAccessNotAvailable: true });
                } else {
                  setState({ isOpenClosedClinicModal: true });
                }
                return;
              }
              window.location.reload();
              return;
            }
          } else {
            // *: Change from Closed to other status
            window.location.reload();
            return;
          }
        }

        // *: Add new facilities
        if (!_.isEmpty(msg.addNewFacilities)) {
          const selectedFacility = {
            label: GETTEXT_ALL_FACILITIES,
            value: 'All facilities',
          };
          facilitySelection.setSelectedFacilityLocalStorage(selectedFacility);
          window.location.reload();
          return;
        }

        // *: Remove facilities
        if (!_.isEmpty(msg.removeFacilities)) {
          const userSupportFacilities = facilitySelection.userSupportFacilities(); // already remove closed clinic
          _.forEach(msg.removeFacilities, (id) =>
            _.remove(userSupportFacilities, (x) => x.id === id),
          );
          if (userSupportFacilities?.length === 0) {
            const integrationBy = auth.getIntegrationBy();
            if (integrationBy === INTERGRATION_ORG.CARDIO_ONE) {
              setState({ isAccessNotAvailable: true });
            } else {
              setState({ isOpenClosedClinicModal: true });
            }
            return;
          }
          const selectedFacility = {
            label: GETTEXT_ALL_FACILITIES,
            value: 'All facilities',
          };
          facilitySelection.setSelectedFacilityLocalStorage(selectedFacility);
          window.location.reload();
          return;
        }

        // *: Change password
        if (msg.isChangedPassword) {
          handleIsChangePassword();
          return;
        }

        if (_.has(msg, 'email')) {
          auth.setEmail(msg.email);
        }
        if (_.has(msg, 'isEmailVerified')) {
          auth.setIsEmailVerified(msg.isEmailVerified);
        }
        forceRerender();
      }
    },
    [state.isLoginWithBiocarePortal],
    () => {
      if (timeOutShowChangePassword.current) {
        clearTimeout(timeOutShowChangePassword.current);
        timeOutShowChangePassword.current = null;
      }
    },
  );

  useEmitter(
    EMITTER_CONSTANTS.ACCESS_REMOVED,
    () => {
      setState({ isOpenAccessRemovedModal: true });
      loadingPageAction();
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.EXPIRED_TOKEN,
    async () => {
      if (isSigningOut.current) {
        return;
      }

      /*
     SPECIAL CASE: Admin/Sales go to portal -->  portal was logged by username
           --> access token was expired --> login by microsoft account
  */
      if (CONFIG.REDIRECT_ADMIN_OR_SALES_URL) {
        if (state.isLoginWithBiocarePortal) {
          auth.logout();
          window.location.href = CONFIG.REDIRECT_ADMIN_OR_SALES_URL;
          return;
        }
      }

      loadingPageAction();
      (SocketIo as any).disconnectSocket();
      try {
        await Auth.signOut();
      } catch (error) {
        console.error('Failed to sign out (expired token): ', error);
      }
      setState({ isOpenExpiredTokenModal: true });
    },
    [state.isLoginWithBiocarePortal],
  );

  useEmitter(
    EMITTER_CONSTANTS.UPDATE_TOKEN_LOCAL_STORAGE,
    (msg: any) => {
      if (msg) {
        changeLocalStorage();
      }
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.DISPLAY_WHAT_IS_NEW_MODAL,
    (msg: any) => {
      if (msg) {
        setState({ isOpenWhatNewPopup: msg.isOpenWhatNewPopup });
      }
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.SITE_UNDER_MAINTENANCE,
    () => {
      loadingPageAction();
      window.location.reload();
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.SITE_UNAUTHORIZED,
    () => {
      loadingPageAction();
      setState({ isUnauthorized: true });
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.MAINTENANCE_CHANGED,
    (msg: any) => {
      if (msg.isEnabled && _.intersection(CLIENT_NAMES, msg.clientNames).length > 0) {
        loadingPageAction();
        window.location.reload();
      }
    },
    [],
  );

  const shouldStopTimer =
    state.isOpenAccessRemovedModal ||
    state.isOpenPasswordChangedModal ||
    state.isOpenDeactivatedAccountModal ||
    state.isOpenEventBucketsChangedModal ||
    state.isOpenClosedClinicModal ||
    state.isOpenExpiredTokenModal;

  return {
    state,
    setState,
    loginFrom,
    isLoginThroughAdminOrSales,
    willShowWhatNewPopup,
    willViewTutorialLater,
    shouldStopTimer,
    onCancelVerifyEmailModal,
    displayWhatNewPopup,
    handleSignOutBySocket,
    handleBackToBiocarePortal,
    handleSignInAgainClick,
    handleOkayDeactivatedAccountModal,
    handleOnlySignOutBiocarePortal,
    handleRedirectToBiocarePortal,
    handleSignOutBiocarePortal,
    meRedux,
  };
};
